import { useEffect, useState } from 'react';

import {
  disconnectDesktopSync,
  getDesktopSyncConnection,
  getDesktopSyncJob,
  pollDesktopSyncPairing,
  startDesktopSessionSync,
  startDesktopSyncPairing,
} from '../../services/api.js';
import styles from './AccountSettings.module.css';


function formatBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}


export default function DesktopSyncSettings({ sessionId }) {
  const [connection, setConnection] = useState({ status: 'loading' });
  const [pairing, setPairing] = useState(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getDesktopSyncConnection()
      .then(setConnection)
      .catch(err => { setConnection({ status: 'error' }); setError(err.message); });
  }, []);

  useEffect(() => {
    if (!pairing || pairing.status !== 'pending') return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const next = await pollDesktopSyncPairing();
        if (cancelled) return;
        if (next.status === 'paired') {
          setConnection(next);
          setPairing(null);
        } else {
          setPairing(previous => ({ ...previous, ...next }));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) timer = setTimeout(poll, 3000);
      }
    };
    timer = setTimeout(poll, 3000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pairing?.status]);

  useEffect(() => {
    if (!job || !['queued', 'preparing', 'uploading', 'importing'].includes(job.status)) return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const next = await getDesktopSyncJob(job.id);
        if (!cancelled) setJob(next);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) timer = setTimeout(poll, 1000);
      }
    };
    timer = setTimeout(poll, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [job?.id, job?.status]);

  async function beginPairing() {
    setError('');
    try {
      const started = await startDesktopSyncPairing();
      setPairing(started);
      setConnection({ status: 'unpaired' });
    } catch (err) {
      setError(err.message);
    }
  }

  async function disconnect() {
    setError('');
    try {
      await disconnectDesktopSync();
      setConnection({ status: 'unpaired' });
      setPairing(null);
    } catch (err) {
      setError(err.message);
    }
  }

  async function uploadSession() {
    setError('');
    try {
      setJob(await startDesktopSessionSync(sessionId));
    } catch (err) {
      setError(err.message);
    }
  }

  const busy = job && ['queued', 'preparing', 'uploading', 'importing'].includes(job.status);
  const percent = job?.total_bytes
    ? Math.min(100, Math.round((job.bytes_sent / job.total_bytes) * 100))
    : 0;

  return (
    <>
      <div className={styles.syncCard}>
        <span className={styles.fieldLabel}>Hosted account</span>
        {connection.status === 'loading' && <p className={styles.syncHint}>Checking connection…</p>}
        {connection.status === 'paired' && (
          <>
            <p className={styles.syncStatus}>Connected to hosted TDA</p>
            {connection.account_email && (
              <p className={styles.syncHint}>Hosted account: {connection.account_email}</p>
            )}
            <p className={styles.syncHint}>Device: {connection.device_name}</p>
            <p className={styles.syncHint}>{connection.server_url}</p>
            <button className={styles.cancelBtn} onClick={disconnect}>Disconnect</button>
          </>
        )}
        {connection.status === 'unpaired' && !pairing && (
          <>
            <p className={styles.syncHint}>
              Connect once, then upload telemetry, rendered video, and original camera files when a faster connection is available.
            </p>
            <button className={styles.saveBtn} onClick={beginPairing}>Connect hosted account</button>
          </>
        )}
        {pairing?.status === 'pending' && (
          <>
            <p className={styles.syncHint}>Confirm this computer using code:</p>
            <strong className={styles.syncCode}>{pairing.user_code}</strong>
            <a
              className={styles.saveBtn}
              href={pairing.verification_uri_complete}
              target="_blank"
              rel="noreferrer"
            >Open approval page</a>
            <p className={styles.syncHint}>This window will notice automatically after approval.</p>
          </>
        )}
      </div>

      {connection.status === 'paired' && (
        <div className={styles.syncCard}>
          <span className={styles.fieldLabel}>Current session</span>
          {!sessionId && <p className={styles.syncHint}>Open a session to upload it.</p>}
          {sessionId && (
            <button className={styles.saveBtn} onClick={uploadSession} disabled={busy}>
              {busy ? 'Uploading…' : 'Upload full session to server'}
            </button>
          )}
          {job && (
            <>
              {busy && (
                <div className={styles.syncProgress}>
                  <div style={{ width: `${percent}%` }} />
                </div>
              )}
              <p className={styles.syncHint}>
                {job.status === 'preparing' && 'Preparing and hashing source files…'}
                {job.status === 'uploading' && `${percent}% · ${formatBytes(job.bytes_sent)} of ${formatBytes(job.total_bytes)}${job.filename ? ` · ${job.filename}` : ''}`}
                {job.status === 'importing' && 'Upload complete. The server is rebuilding the session…'}
                {job.status === 'uploaded' && 'Source files uploaded.'}
                {job.status === 'imported' && 'Session is available in your hosted account.'}
                {job.status === 'failed' && `Upload failed: ${job.error}`}
              </p>
            </>
          )}
        </div>
      )}

      {error && <span className={styles.errorMsg}>{error}</span>}
    </>
  );
}
